import React, { useEffect } from 'react';
import { Button, Modal, Form, Alert, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import './ScheduleModals.scss';

const TimeOffRequestModal = ({ show, onHide, mode, editData, absenceTypes, onSubmit, submitting, submitError }) => {
    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm({ defaultValues: { isPartialDay: false } });

    const isPartialDay = watch('isPartialDay');
    const startDateValue = watch('startDate');
    const partialDayStartValue = watch('partialDayStart');

    useEffect(() => {
        if (show) {
            if (mode === 'edit' && editData) {
                reset({
                    absenceTypeID: editData.absenceTypeID || '',
                    startDate: editData.startDate || '',
                    endDate: editData.endDate || '',
                    isPartialDay: editData.isPartialDay || false,
                    partialDayStart: editData.partialDayStart || '',
                    partialDayEnd: editData.partialDayEnd || '',
                    notes: editData.notes || '',
                });
            } else {
                reset({ isPartialDay: false });
            }
        }
    }, [show, mode, editData, reset]);

    const handleFormSubmit = (data) => {
        onSubmit({
            absenceTypeID: parseInt(data.absenceTypeID, 10),
            startDate: data.startDate,
            endDate: data.endDate,
            isPartialDay: data.isPartialDay,
            partialDayStart: data.isPartialDay ? data.partialDayStart : null,
            partialDayEnd: data.isPartialDay ? data.partialDayEnd : null,
            notes: data.notes || null,
        });
    };

    return (
        <Modal show={show} onHide={onHide} centered className="schedule-modal">
            <Modal.Header closeButton>
                <Modal.Title>{mode === 'edit' ? 'Edit Time Off Request' : 'New Time Off Request'}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit(handleFormSubmit)}>
                <Modal.Body>
                    {submitError && <Alert variant="danger">{submitError}</Alert>}

                    <Form.Group className="mb-3">
                        <Form.Label>
                            Reason <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            as="select"
                            {...register('absenceTypeID', { required: 'Reason is required.' })}
                            isInvalid={!!errors.absenceTypeID}
                        >
                            <option value="">Select a reason…</option>
                            {absenceTypes.map((type) => (
                                <option key={type.id} value={type.id}>
                                    {type.description}
                                </option>
                            ))}
                        </Form.Control>
                        {errors.absenceTypeID && (
                            <Form.Control.Feedback type="invalid">{errors.absenceTypeID.message}</Form.Control.Feedback>
                        )}
                    </Form.Group>

                    <div className="row mb-3">
                        <Col xs={12} md={6} className="mb-3 mb-md-0">
                            <Form.Group>
                                <Form.Label>
                                    Start Date <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    {...register('startDate', { required: 'Start date is required.' })}
                                    isInvalid={!!errors.startDate}
                                />
                                {errors.startDate && (
                                    <Form.Control.Feedback type="invalid">
                                        {errors.startDate.message}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Group>
                                <Form.Label>
                                    End Date <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    {...register('endDate', {
                                        required: 'End date is required.',
                                        validate: (value) =>
                                            !startDateValue ||
                                            value >= startDateValue ||
                                            'End date must be on or after start date.',
                                    })}
                                    isInvalid={!!errors.endDate}
                                />
                                {errors.endDate && (
                                    <Form.Control.Feedback type="invalid">
                                        {errors.endDate.message}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </Col>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="schedule-isPartialDay"
                            label="Partial day"
                            {...register('isPartialDay')}
                        />
                    </Form.Group>

                    {isPartialDay && (
                        <div className="row mb-3">
                            <Col xs={12} md={6} className="mb-3 mb-md-0">
                                <Form.Group>
                                    <Form.Label>
                                        From <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="time"
                                        {...register('partialDayStart', {
                                            required: isPartialDay ? 'Start time is required.' : false,
                                        })}
                                        isInvalid={!!errors.partialDayStart}
                                    />
                                    {errors.partialDayStart && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.partialDayStart.message}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={6}>
                                <Form.Group>
                                    <Form.Label>
                                        To <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="time"
                                        {...register('partialDayEnd', {
                                            required: isPartialDay ? 'End time is required.' : false,
                                            validate: (value) =>
                                                !isPartialDay ||
                                                !partialDayStartValue ||
                                                !value ||
                                                value > partialDayStartValue ||
                                                'End time must be after start time.',
                                        })}
                                        isInvalid={!!errors.partialDayEnd}
                                    />
                                    {errors.partialDayEnd && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.partialDayEnd.message}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                        </div>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control as="textarea" rows={3} placeholder="Optional notes…" {...register('notes')} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={submitting} className="schedule-modal__btn">
                        {mode === 'edit' ? 'Back' : 'Cancel'}
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={submitting}
                        className="schedule-modal__btn schedule-modal__btn--primary"
                    >
                        {submitting
                            ? mode === 'edit'
                                ? 'Saving…'
                                : 'Submitting…'
                            : mode === 'edit'
                            ? 'Save Changes'
                            : 'Submit Request'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default TimeOffRequestModal;
